import { useEffect, useState } from "react";
import { apiGet } from "lib/apiClient";
import { formatKes } from "lib/format";
import { Dialog } from "components/ui/Dialog";

/**
 * Dismissible, re-openable instructions slideshow for the create-market flow.
 * Every KES amount and time window here comes from the live GET /admin/market-creation-policy
 * response, never a hardcoded number — an admin can change or waive the policy at any time,
 * and a stale hardcoded figure here would quietly mislead a partner about what they're
 * agreeing to.
 */
export default function HowItWorksModal({ onClose }) {
  const [slide, setSlide] = useState(0);
  const [policy, setPolicy] = useState(null);
  const [policyError, setPolicyError] = useState(null);

  useEffect(() => {
    apiGet("/admin/market-creation-policy")
      .then((res) => setPolicy(res.policy))
      .catch((err) => setPolicyError(err.message || "Couldn't load the current policy."));
  }, []);


  const slides = [
    {
      title: "Write a clear question",
      body: (
        <>
          <p>Pick a question that resolves to a plain YES or NO, with a firm deadline and a source that can actually verify the answer.</p>
          <p className="mt-2">
            Name the exact competition, season, or reporting period — "this season" or "soon" leaves room for disagreement later, and that
            disagreement is exactly what a dispute is.
          </p>
        </>
      ),
    },
    {
      title: "What it costs",
      body: (
        <>
          <p>
            <strong>Nothing.</strong> Creating a market is free — there is no starting contribution and no bond, and your wallet is not
            touched at any point in this flow.
          </p>
          <p className="mt-2">
            The market is funded entirely by the people who trade it. Your earnings come from your share of the revenue it generates.
          </p>
        </>
      ),
    },
    {
      title: "Draft → review → publish",
      body: (
        <>
          <p>Every market starts as a <strong>Draft</strong> — only visible to you. Edit anything until you're ready.</p>
          <p className="mt-2">
            When ready, submit it for approval from your markets list. Our review team checks it before it opens to traders — you'll see
            its status change as it moves through review.
          </p>
        </>
      ),
    },
    {
      title: "The dispute window",
      body: (
        <>
          <p>
            Once a result is proposed for your market, a second reviewer has to approve it before it can be finalized.
          </p>
          <p className="mt-2">
            A result that is rejected sends the market back so a corrected one can be proposed.
          </p>
        </>
      ),
    },
  ];

  return (
    <Dialog open onClose={onClose} title="How market creation works">
      <style>{`
        @keyframes howItWorksSlideIn { from { opacity: 0; transform: translateX(16px); } to { opacity: 1; transform: translateX(0); } }
        .how-it-works-slide { animation: howItWorksSlideIn 0.25s ease-out; }
      `}</style>
        <div key={slide} className="how-it-works-slide min-h-[160px]">
          <p className="text-xs font-medium text-muted-foreground">
            Step {slide + 1} of {slides.length}
          </p>
          <p className="mt-1 text-base font-semibold text-foreground">{slides[slide].title}</p>
          <div className="mt-2 text-sm text-muted-foreground">{slides[slide].body}</div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex gap-1.5">
            {slides.map((s, i) => (
              <button
                key={s.title}
                onClick={() => setSlide(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-2 w-2 rounded-full transition-all duration-300 ${i === slide ? "w-5 bg-primary" : "bg-border"}`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setSlide((s) => Math.max(0, s - 1))}
              disabled={slide === 0}
              className="inline-flex h-9 items-center rounded-sm border border-border bg-background px-4 text-sm font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-40 dark:bg-input/30"
            >
              Back
            </button>
            {slide < slides.length - 1 ? (
              <button onClick={() => setSlide((s) => Math.min(slides.length - 1, s + 1))} className="inline-flex h-9 items-center rounded-sm bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90">
                Next
              </button>
            ) : (
              <button onClick={onClose} className="inline-flex h-9 items-center rounded-sm bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90">
                Got it
              </button>
            )}
          </div>
        </div>
    </Dialog>
  );
}
